import React, { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  Button,
  TextField,
  Box,
} from '@mui/material';
import './FontManagement.css';
import { getApiUrl } from '../utils/api';
import { useProject } from '../contexts/ProjectContext';

export interface LogoInfo {
  name: string;
  file: File | null;
  url: string;
  enabled: boolean;
}

interface LogoManagementProps {
  onLogoListChange: (logos: LogoInfo[]) => void;
  initialLogos?: LogoInfo[];
  onLogoUploaded?: (logoName: string, serverUrl: string) => void;
}

const LogoManagement: React.FC<LogoManagementProps> = ({ onLogoListChange, initialLogos = [], onLogoUploaded }) => {
  const { selectedProject } = useProject();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [logoSearch, setLogoSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const availableLogosRef = useRef<LogoInfo[]>([]);
  const logosLoadingRef = useRef<Promise<LogoInfo[]> | null>(null);

  // Lazy load available logos only when needed
  const getAvailableLogos = async (): Promise<LogoInfo[]> => {
    // Return cached result if already loaded
    if (availableLogosRef.current.length > 0) {
      return availableLogosRef.current;
    }

    // Return existing promise if already loading
    if (logosLoadingRef.current) {
      return logosLoadingRef.current;
    }

    // Start loading
    logosLoadingRef.current = (async () => {
      try {
        const response = await fetch(`${getApiUrl()}/available-logos?projectId=${selectedProject?.id}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();
        if (result.status === 'OK') {
          const availableLogos: LogoInfo[] = result.logos.map((logo: any) => ({
            name: logo.name,
            file: null, // No file object for server logos
            url: `${window.location.origin}${logo.url}`,
            enabled: false // Start with disabled
          }));

          // Cache the result
          availableLogosRef.current = availableLogos;
          return availableLogos;
        }
      } catch (err) {
        console.error('Failed to load available logos:', err);
      }

      return [];
    })();

    return logosLoadingRef.current;
  };

  // Initialize state with lazy loading
  const [logos, setLogos] = useState<LogoInfo[]>(() => {
    // Start loading available logos in the background
    getAvailableLogos().then(availableLogos => {
      if (availableLogos.length > 0) {
        setLogos(prevLogos => {
          // If initialLogos was empty, populate with all available logos (enabled by default)
          if (prevLogos.length === 0 && initialLogos.length === 0) {
            const enabledAvailableLogos = availableLogos.map(logo => ({
              ...logo,
              enabled: true // Enable all available logos when no initial logos are provided
            }));
            // Notify parent component of the change
            setTimeout(() => onLogoListChange(enabledAvailableLogos), 0);
            return enabledAvailableLogos;
          }

          // Merge with existing logos, avoiding duplicates
          const existingNames = new Set(prevLogos.map(l => l.name.toLowerCase()));
          const newLogos = availableLogos.filter(l => !existingNames.has(l.name.toLowerCase()));

          if (newLogos.length > 0) {
            const updated = [...prevLogos, ...newLogos];
            // Notify parent component of the change
            setTimeout(() => onLogoListChange(updated), 0);
            return updated;
          }

          return prevLogos;
        });
      }
    });

    return initialLogos;
  });

  // Update when initialLogos changes
  useEffect(() => {
    if (initialLogos.length === 0) {
      // If initialLogos is empty, check if we should populate with available logos
      getAvailableLogos().then(availableLogos => {
        if (availableLogos.length > 0) {
          const enabledAvailableLogos = availableLogos.map(logo => ({
            ...logo,
            enabled: true // Enable all available logos when no initial logos are provided
          }));
          setLogos(enabledAvailableLogos);
          onLogoListChange(enabledAvailableLogos);
        } else {
          setLogos([]);
        }
      });
    } else {
      setLogos(initialLogos);
    }
  }, [initialLogos, onLogoListChange]);

  const uploadLogoToAPI = async (file: File) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${getApiUrl()}/upload-logo?projectId=${selectedProject!.id}`, { method: 'POST', body: formData });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      if (result.status === 'OK') {
        setError(null);
        // Build server logo URL as: <server-origin>/logos/<filename>
        const serverLogoUrl = `${window.location.origin}/logos/${encodeURIComponent(file.name)}`;
        if (onLogoUploaded) {
          const cleanName = file.name.replace(/\.[^/.]+$/, '');
          onLogoUploaded(cleanName, serverLogoUrl);
        }
      } else {
        setError(result.message || 'Unknown error occurred');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const existingNamesLower: Set<string> = new Set(logos.map((l) => l.name.toLowerCase()));
    const addedNamesLower: Set<string> = new Set<string>();
    const newLogos: LogoInfo[] = [];

    Array.from(files).forEach((file) => {
      const logoName = file.name.replace(/\.[^/.]+$/, '');
      const nameLower = logoName.toLowerCase();

      if (existingNamesLower.has(nameLower) || addedNamesLower.has(nameLower)) {
        return;
      }

      addedNamesLower.add(nameLower);
      const url = URL.createObjectURL(file);

      // Send to backend
      uploadLogoToAPI(file);

      newLogos.push({ name: logoName, file, url, enabled: true });
    });

    if (newLogos.length > 0) {
      const updated = [...logos, ...newLogos];
      setLogos(updated);
      onLogoListChange(updated);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleToggleLogo = (idx: number) => {
    const updated = logos.map((logo, i) => (i === idx ? { ...logo, enabled: !logo.enabled } : logo));
    setLogos(updated);
    onLogoListChange(updated);
  };

  const toggleCollapsed = () => setIsCollapsed((prev) => !prev);

  const filteredLogos = logos
    .map((l, i) => ({ ...l, idx: i }))
    .filter((l) => l.name.toLowerCase().includes(logoSearch.toLowerCase()));

  const handleOpen = () => setTimeout(() => searchInputRef.current?.focus(), 60);
  const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

  const handleSelectAll = (filtered: typeof filteredLogos) => {
    const allSelected = filtered.every((l) => l.enabled);
    const updated = logos.map((l) => (filtered.some((fl) => fl.name === l.name) ? { ...l, enabled: !allSelected } : l));
    setLogos(updated);
    onLogoListChange(updated);
  };

  return (
    <div className="font-management">
      <div className="settings-header" onClick={toggleCollapsed}>
        <span className="settings-header-title">{t`Logo Management`}</span>
        <button
          type="button"
          className="settings-header-chevron"
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          onClick={(e) => {
            e.stopPropagation();
            toggleCollapsed();
          }}
        >
          <KeyboardArrowUpIcon className={`chevron-icon ${isCollapsed ? '' : 'expanded'}`} />
        </button>
      </div>

      {!isCollapsed && (
        <div className="font-management-group">
          <div className="setting-item upload-section">
            <label className="upload-label">
              <span className="upload-title">{t`Upload Logos`}</span>
              <div className="tooltip-wrapper">
                <span className="tooltip-icon" style={{ marginLeft: '4px' }}>?</span>
                <div className="tooltip-text" style={{ paddingTop: '5px', height: '33px', width: '350px', maxWidth: '350px' }}>
                  {t`Supported formats: .png, .jpg, .jpeg, .svg, .webp`}
                </div>
              </div>
            </label>

            <div className="upload-controls">
              <input
                ref={fileInputRef}
                id="logo-upload-input"
                type="file"
                accept=".png,.jpg,.jpeg,.svg,.webp"
                multiple
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
              />

              <Button
                variant="contained"
                onClick={() => fileInputRef.current?.click()}
                disableElevation
                disableRipple
                aria-label="Upload logos"
                sx={{
                  width: 44,
                  height: 44,
                  minWidth: 44,
                  padding: 0,
                  borderRadius: 2,
                  backgroundColor: '#1976d2',
                  color: '#fff',
                  border: '2px solid #1976d2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.18s ease-in-out',
                  '&:hover': { backgroundColor: '#fff', color: '#1976d2', borderColor: '#1976d2', transform: 'scale(1.05)' },
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="currentColor"
                  style={{ display: 'block' }}
                >
                  <path d="M11.293 3.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L13 6.414V16a1 1 0 1 1-2 0V6.414L8.707 8.707a1 1 0 0 1-1.414-1.414l4-4Z" />
                  <path d="M6 17a1 1 0 1 0-2 0v.6C4 19.482 5.518 21 7.4 21h9.2c1.882 0 3.4-1.518 3.4-3.4V17a1 1 0 1 0-2 0v.6c0 .778-.622 1.4-1.4 1.4H7.4c-.778 0-1.4-.622-1.4-1.4V17Z" />
                </svg>
              </Button>
            </div>

            {loading && <div style={{ marginTop: '8px', color: '#1976d2' }}>{t`Uploading logo...`}</div>}
            {error && <div style={{ marginTop: '8px', color: 'red' }}>{error}</div>}
          </div>

          <div className="setting-item font-list-section">
            <label className="font-list-label">{t`Logo List`}</label>

            <div className="tooltip-wrapper">
              <span className="tooltip-icon" style={{ marginLeft: '-4px' }}>?</span>
              <div
                className="tooltip-text-ratio"
                style={{ width: '30vh', maxWidth: 'none', whiteSpace: 'normal', wordWrap: 'break-word' }}
              >
                {t`You can select logos in the excel data by clicking the cell`}
              </div>
            </div>

            <FormControl className="font-list-select" fullWidth>
              <Select
                sx={{ height: '40px', '& .MuiSelect-icon': { color: '#1976d2' } }}
                multiple
                value={logos.filter(l => l.enabled).map(l => l.name)}
                renderValue={(selected) => (
                  <Box sx={{ height: '50%', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {(selected as string[]).length ? (selected as string[]).join(', ') : t`None`}
                  </Box>
                )}
                onOpen={handleOpen}
                onClose={() => setLogoSearch('')}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 260, borderRadius: 8, overflow: 'auto' } },
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  autoFocus: false,
                  MenuListProps: { onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation() },
                }}
              >
                {/* Search Input */}
                <MenuItem disableRipple onClick={stopPropagation} sx={{ px: 1, py: 0.5 }}>
                  <Box onMouseDown={(e) => e.stopPropagation()} sx={{ width: '100%' }}>
                    <TextField
                      inputRef={searchInputRef}
                      value={logoSearch}
                      onChange={(e) => setLogoSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      onKeyUp={(e) => e.stopPropagation()}
                      placeholder={String(t`Search logos...`)}
                      size="small"
                      variant="standard"
                      fullWidth
                      InputProps={{ disableUnderline: true, style: { fontSize: 13, paddingLeft: 6 } }}
                    />
                  </Box>
                </MenuItem>

                {filteredLogos.length > 0 && (
                  <MenuItem
                    disableRipple
                    onClick={(e) => { e.stopPropagation(); handleSelectAll(filteredLogos); }}
                    sx={{ marginTop: '4px', marginLeft: '28px', width: '75%', px: 1, py: 0.5, fontWeight: 500, cursor: 'pointer', borderRadius: 1, backgroundColor: '#e3f2fd', '&:hover': { backgroundColor: '#bbdefb' }, mb: 0.5, display: 'flex', justifyContent: 'center' }}
                  >
                    {filteredLogos.every(l => l.enabled) ? t`Unselect All` : t`Select All`}
                  </MenuItem>
                )}

                {logos.length === 0 && <MenuItem disabled>{t`No logos uploaded yet.`}</MenuItem>}
                {filteredLogos.length === 0 && logos.length > 0 && <MenuItem disabled>{t`No logos match your search.`}</MenuItem>}

                {filteredLogos.map((logo) => (
                  <MenuItem
                    key={`${logo.name}-${logo.idx}`}
                    value={logo.name}
                    onClick={(e) => { e.stopPropagation(); handleToggleLogo(logo.idx); }}
                    sx={{ borderRadius: 0 }}
                  >
                    <Checkbox
                      checked={logo.enabled}
                      onClick={(e) => { e.stopPropagation(); handleToggleLogo(logo.idx); }}
                      disableRipple
                    />
                    <ListItemText
                      primary={logo.name}
                      primaryTypographyProps={{ style: { fontFamily: 'inherit' } }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogoManagement;

